<template>
  <div
    class="date-range-picker"
    :class="{
      'has-error': error,
    }"
  >
    <FormLabel :is-required="isRequired">{{ label }}</FormLabel>
    <!-- On phones, the calendar in a sheet, as the other date pickers (docs/page-layout.md). -->
    <v-menu
      ref="menu"
      :disabled="isPhone"
      :close-on-content-click="false"
      :return-value.sync="dates"
      transition="scale-transition"
      offset-y
      min-width="auto"
    >
      <template v-slot:activator="{ on, attrs }">
        <!-- The activator listeners sit on a wrapper: bound to the v-text-field
             they'd only reach the inner <input>, leaving the icon and the
             field's chrome dead to clicks. -->
        <div class="date-range-picker__activator" :class="{ disabled }" v-on="disabled ? {} : on" @click="openSheet">
          <v-text-field
            class="date-range-picker__input"
            :disabled="disabled"
            :value="datesRangeText"
            :placeholder="datesRangeText"
            :hint="!error ? hint : ''"
            :persistent-hint="!error"
            append-icon="mdi-calendar"
            readonly
            v-bind="attrs"
            solo
            flat
            dense
          ></v-text-field>
        </div>
      </template>
      <DatePicker
        ref="datePicker"
        :propDates="dates"
        range
        @on-close-menu="closeMenu"
        @on-save-dates="saveDates"
        @on-clear-dates="clearDates"
      />
    </v-menu>
    <BaseBottomSheet v-if="isPhone" v-model="sheetOpen" :title="label || placeholder">
      <DatePicker
        v-if="sheetOpen"
        :propDates="dates"
        range
        @on-close-menu="closeMenu"
        @on-save-dates="saveDates"
        @on-clear-dates="clearDates"
      />
    </BaseBottomSheet>
    <span v-if="error" class="date-range-picker__error">{{ error }}</span>
  </div>
</template>

<script>
import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';
import DatePicker from '@/components/form/DatePicker';
import FormLabel from '@/components/form/FormLabel';
import { viewport } from '@/util/viewport';

export default {
  name: 'FormDateRangePicker',
  components: {
    BaseBottomSheet,
    DatePicker,
    FormLabel,
  },
  $_veeValidate: {
    value() {
      return this.value;
    },
  },
  props: {
    name: {
      type: String,
      required: true,
    },
    value: {
      type: null,
      default: null,
    },
    label: {
      type: String,
      required: false,
    },
    values: {
      type: Array,
      required: false,
      default: () => [],
    },
    hint: {
      type: String,
      required: false,
      default() {
        return this.$t('date-picker.format');
      },
    },
    placeholder: {
      type: String,
      required: false,
      default() {
        return this.$t('date-picker.placeholder');
      },
    },
    error: {
      type: String,
      required: false,
      default: null,
    },
    isRequired: Boolean,
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      menu: false,
      sheetOpen: false,
      dates: this.values,
    };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    datesRangeText() {
      const dates = [...this.dates].sort((a, b) => new Date(a) - new Date(b)).join(' - ');
      return dates || this.placeholder;
    },
    attrs() {
      const { rows, ...attrs } = this.$attrs;
      return attrs;
    },
  },
  methods: {
    openSheet() {
      if (this.isPhone && !this.disabled) this.sheetOpen = true;
    },
    closeMenu() {
      this.menu = false;
      this.sheetOpen = false;
    },
    // The menu keeps them as it closes (its return value); the sheet, here.
    keepDates(dates) {
      if (this.isPhone) {
        this.dates = dates;
        this.sheetOpen = false;
      } else {
        this.$refs.menu.save(dates);
      }
    },
    clearDatePickerDates() {
      if (this.$refs.datePicker) {
        this.$refs.datePicker.clearDates();
      }
    },
    clearDates() {
      this.dates = [];
      this.keepDates(this.dates);
      this.$emit('updated-dates', this.dates);
    },
    saveDates(sentDates) {
      const dates = sentDates.sort((a, b) => new Date(a) - new Date(b));
      this.keepDates(dates);
      this.$emit('updated-dates', dates);
    },
  },
};
</script>

<style lang="scss">
.date-range-picker {
  height: 90px;
  position: relative;
  width: 100%;

  // The whole field opens the picker (the input itself is readonly).
  &__activator {
    cursor: pointer;

    input {
      cursor: pointer;
    }

    &.disabled {
      cursor: not-allowed;

      input {
        cursor: not-allowed;
      }
    }
  }

  .v-input {
    &__slot {
      background: $color-neutral-pale !important;
      border: 1px solid $color-border;
      border-radius: $radius-md;
      color: $color-normal;
      font-size: 14px !important;
      min-height: 42px;
      outline: none;
      padding: 0 12px !important;
      transition: background 200ms ease, border 200ms ease, box-shadow 200ms ease;
      align-items: center !important;

      &:hover {
        border-color: $color-normal-pale;
      }
    }
    &--is-focused {
      .v-input__slot {
        border-color: $color-accent;
        box-shadow: 0 0 0 3px rgba($color-accent-500, 0.1);
      }
    }
  }

  .v-input__append-inner {
    margin-top: 0px !important;
    padding: 0 !important;

    .v-icon {
      color: $color-normal;
      height: 5px;
      object-fit: contain;
      width: 10px;
    }
  }

  &__error {
    font-family: Lato;
    font-size: 12px;
    font-weight: normal;
    font-stretch: normal;
    font-style: italic;
    line-height: 1.5;
    letter-spacing: normal;
    color: $color-error;
    bottom: 0;
    right: 0;
    position: absolute;
  }

  &.has-error {
    .v-input__slot {
      border-color: $color-error;
    }
  }
}
.v-picker {
  .v-btn {
    &.accent {
      background: $color-accent !important;
    }
  }

  &__actions {
    padding: 0;
  }
}
</style>
